// @flow
import { useCallback } from 'react';
import classnames from 'classnames';

import FormattedPrice from '@components/FormattedPrice';

import * as routes from '@src/app_routes/ringBuilder';
import css from '@styles/dyor';

import type { DyorSettingType } from '@types/setting';
import type { DyorDiamondType } from '@types/diamond';

const placeholderRing = '/images/ring-builder/ring.png';

type ImagePropsType = {
  setting: DyorSettingType
};

type SpecsPropsType = {
  diamond: DyorDiamondType,
  setting: DyorSettingType
};

type EmptyPropsType = {
  active: boolean
};

type PropsType = {
  diamond: ?DyorDiamondType,
  setting: ?DyorSettingType,
  active: boolean,
  onClick: () => void
};

function EmptyCombined({ active }: EmptyPropsType) {
  return (
    <section
      className={classnames(css.section, 'empty', {
        active
      })}
    >
      <PlaceholderImage />
      <div className={css.specifications}>
        <h3 className={css.sectionHeader}>
          <span className={css.sectionPronoun}>Your </span>Ring
        </h3>
      </div>
    </section>
  );
}

function CombinedImage({ setting }: ImagePropsType) {
  return (
    <div className={css.imageContainer}>
      <img
        src={setting.fully_set_image.thumbnail}
        alt='Engagement ring image'
      />
    </div>
  );
}

function PlaceholderImage() {
  return (
    <div className={css.imageContainer}>
      <img src={placeholderRing} alt='Placeholder engagement ring image' />
    </div>
  );
}

function CombinedSpecifications({ setting, diamond }: SpecsPropsType) {
  return (
    <div className={css.specifications}>
      <h3 className={css.sectionHeader}>
        <span className={css.sectionPronoun}>Your </span>Ring
      </h3>
      <p className={css.sectionCaption}>
        <FormattedPrice
          value={
            parseFloat(setting.retail_price) + parseFloat(diamond.retail_price)
          }
        />
      </p>
    </div>
  );
}

export default function Combined({
  setting,
  diamond,
  active,
  onClick
}: PropsType) {
  const handleClick = useCallback(() => {
    onClick();
    if (setting && diamond && window)
      window.location = routes.combinedStep({
        diamond: diamond.list_service_diamond_id,
        setting: setting.vendor_sku
      });
  }, [setting, diamond]);

  if (!setting || !diamond) {
    return <EmptyCombined active={active} onClick={onClick} />;
  }

  return (
    <section
      className={classnames(css.section, { active })}
      onClick={handleClick}
    >
      <CombinedImage setting={setting} />
      <CombinedSpecifications
        diamond={diamond}
        setting={setting}
        onClick={onClick}
      />
    </section>
  );
}
